import { Quote, QuoteLineItem } from '@/types/quote';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface CompanySettings {
  name?: string;
  street?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  kvk?: string;
  btw?: string;
  iban?: string;
  bic?: string;
  phone?: string;
  email?: string;
  website?: string;
}

interface PlaceholderData {
  quote: Quote;
  lineItems?: QuoteLineItem[];
  ownerName?: string;
  organizationKvk?: string;
  organizationBtw?: string;
  organizationAddresses?: Array<{
    street?: string;
    house_number?: string;
    postal_code?: string;
    city?: string;
    country?: string;
  }>;
  companySettings?: CompanySettings;
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'dd-MM-yyyy', { locale: nl });
  } catch {
    return '';
  }
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const replacePlaceholders = (
  text: string,
  data: PlaceholderData
): string => {
  let result = text;
  const { quote, ownerName, organizationKvk, organizationBtw, organizationAddresses, companySettings } = data;
  
  // Company (own business) placeholders
  const company = companySettings || {};
  const companyStreet = `${company.street || ''} ${company.house_number || ''}`.trim();
  const companyFullAddress = company.street 
    ? `${companyStreet}, ${company.postal_code || ''} ${company.city || ''}`.trim()
    : '';
  
  result = result.replace(/\{\{company_name\}\}/g, company.name || '');
  result = result.replace(/\{\{company_street\}\}/g, companyStreet);
  result = result.replace(/\{\{company_postal_code\}\}/g, company.postal_code || '');
  result = result.replace(/\{\{company_city\}\}/g, company.city || '');
  result = result.replace(/\{\{company_country\}\}/g, company.country || '');
  result = result.replace(/\{\{company_full_address\}\}/g, companyFullAddress);
  result = result.replace(/\{\{company_kvk\}\}/g, company.kvk || '');
  result = result.replace(/\{\{company_btw\}\}/g, company.btw || '');
  result = result.replace(/\{\{company_iban\}\}/g, company.iban || '');
  result = result.replace(/\{\{company_bic\}\}/g, company.bic || '');
  result = result.replace(/\{\{company_phone\}\}/g, company.phone || '');
  result = result.replace(/\{\{company_email\}\}/g, company.email || '');
  result = result.replace(/\{\{company_website\}\}/g, company.website || '');
  
  // Organization (client) placeholders
  if (quote.organization) {
    result = result.replace(/\{\{organization_name\}\}/g, quote.organization.name || '');
    result = result.replace(/\{\{organization_phone\}\}/g, (quote.organization as any).phone || '');
    result = result.replace(/\{\{organization_email\}\}/g, (quote.organization as any).email || '');
  } else {
    result = result.replace(/\{\{organization_name\}\}/g, '');
    result = result.replace(/\{\{organization_phone\}\}/g, '');
    result = result.replace(/\{\{organization_email\}\}/g, '');
  }
  
  result = result.replace(/\{\{organization_kvk\}\}/g, organizationKvk || '');
  result = result.replace(/\{\{organization_btw\}\}/g, organizationBtw || '');
  
  // Organization address placeholders
  const primaryAddress = organizationAddresses?.[0];
  if (primaryAddress) {
    const street = `${primaryAddress.street || ''} ${primaryAddress.house_number || ''}`.trim();
    result = result.replace(/\{\{organization_street\}\}/g, street);
    result = result.replace(/\{\{organization_postal_code\}\}/g, primaryAddress.postal_code || '');
    result = result.replace(/\{\{organization_city\}\}/g, primaryAddress.city || '');
    result = result.replace(/\{\{organization_country\}\}/g, primaryAddress.country || '');
    const fullAddress = `${street}, ${primaryAddress.postal_code || ''} ${primaryAddress.city || ''}`.trim();
    result = result.replace(/\{\{organization_full_address\}\}/g, fullAddress);
  } else {
    result = result.replace(/\{\{organization_street\}\}/g, '');
    result = result.replace(/\{\{organization_postal_code\}\}/g, '');
    result = result.replace(/\{\{organization_city\}\}/g, '');
    result = result.replace(/\{\{organization_country\}\}/g, '');
    result = result.replace(/\{\{organization_full_address\}\}/g, '');
  }
  
  // Contact placeholders
  if (quote.contact) {
    const fullName = `${quote.contact.first_name || ''} ${quote.contact.last_name || ''}`.trim();
    result = result.replace(/\{\{contact_name\}\}/g, fullName);
    result = result.replace(/\{\{contact_first_name\}\}/g, quote.contact.first_name || '');
    result = result.replace(/\{\{contact_last_name\}\}/g, quote.contact.last_name || '');
    result = result.replace(/\{\{contact_email\}\}/g, quote.contact.email || '');
    result = result.replace(/\{\{contact_phone\}\}/g, (quote.contact as any).phone || '');
    result = result.replace(/\{\{contact_mobile\}\}/g, (quote.contact as any).mobile || '');
    result = result.replace(/\{\{contact_function\}\}/g, (quote.contact as any).title || '');
  } else {
    result = result.replace(/\{\{contact_name\}\}/g, '');
    result = result.replace(/\{\{contact_first_name\}\}/g, '');
    result = result.replace(/\{\{contact_last_name\}\}/g, '');
    result = result.replace(/\{\{contact_email\}\}/g, '');
    result = result.replace(/\{\{contact_phone\}\}/g, '');
    result = result.replace(/\{\{contact_mobile\}\}/g, '');
    result = result.replace(/\{\{contact_function\}\}/g, '');
  }
  
  // Quote data
  result = result.replace(/\{\{quote_number\}\}/g, quote.quote_number);
  result = result.replace(/\{\{quote_title\}\}/g, quote.title);
  result = result.replace(/\{\{quote_date\}\}/g, formatDate(quote.created_at));
  result = result.replace(/\{\{valid_until\}\}/g, formatDate(quote.valid_until));
  result = result.replace(/\{\{reference\}\}/g, (quote as any).reference || '');
  result = result.replace(/\{\{version_number\}\}/g, String((quote as any).version_number || 1));
  
  // Financial data
  result = result.replace(/\{\{subtotal\}\}/g, formatCurrency(quote.subtotal_amount));
  result = result.replace(/\{\{vat_amount\}\}/g, formatCurrency(quote.vat_amount));
  result = result.replace(/\{\{total\}\}/g, formatCurrency(quote.total_amount));
  result = result.replace(/\{\{vat_rate\}\}/g, '21%'); // Default Dutch VAT
  result = result.replace(/\{\{discount_amount\}\}/g, formatCurrency((quote as any).discount_amount || 0));
  result = result.replace(/\{\{discount_percentage\}\}/g, String((quote as any).discount_percentage || 0) + '%');
  result = result.replace(/\{\{payment_term\}\}/g, String((quote as any).payment_term || 14));
  
  // Calculate payment due date
  const paymentTerm = (quote as any).payment_term || 14;
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + paymentTerm);
  result = result.replace(/\{\{payment_due_date\}\}/g, formatDate(dueDate.toISOString()));
  
  // Project data
  if (quote.project) {
    result = result.replace(/\{\{project_name\}\}/g, quote.project.name || '');
    result = result.replace(/\{\{project_number\}\}/g, quote.project.project_number || '');
  } else {
    result = result.replace(/\{\{project_name\}\}/g, '');
    result = result.replace(/\{\{project_number\}\}/g, '');
  }
  
  // Quote sections (rich text)
  result = result.replace(/\{\{project_goal\}\}/g, quote.project_goal || '');
  result = result.replace(/\{\{work_description\}\}/g, quote.work_description || '');
  result = result.replace(/\{\{advice_conclusion\}\}/g, quote.advice_conclusion || '');
  
  // Payment conditions - format as readable list
  const paymentConditions = quote.payment_conditions || [];
  if (Array.isArray(paymentConditions) && paymentConditions.length > 0) {
    const conditionsText = paymentConditions
      .map((c: { percentage: number; description: string }) => `${c.percentage}% - ${c.description}`)
      .join('\n');
    result = result.replace(/\{\{payment_conditions\}\}/g, conditionsText);
  } else {
    result = result.replace(/\{\{payment_conditions\}\}/g, '');
  }
  
  // Signature block placeholder - will be rendered specially in PDF
  result = result.replace(/\{\{signature_block\}\}/g, '[HANDTEKENINGBLOK]');
  
  // Misc
  result = result.replace(/\{\{current_date\}\}/g, formatDate(new Date().toISOString()));
  result = result.replace(/\{\{owner_name\}\}/g, ownerName || '');
  
  return result;
};

export interface PlaceholderCategory {
  label: string;
  placeholders: Array<{
    placeholder: string;
    description: string;
    example: string;
  }>;
}

export interface PlaceholderStatus {
  placeholder: string;
  description: string;
  category: string;
  isFilled: boolean;
  value: string;
}

// Extract all placeholder patterns from text
export const extractPlaceholdersFromText = (text: string): string[] => {
  if (!text) return [];
  const regex = /\{\{(\w+)\}\}/g;
  const matches = [...text.matchAll(regex)];
  return [...new Set(matches.map(m => `{{${m[1]}}}`))];
};

// Analyze which placeholders are filled vs missing
export const analyzeQuotePlaceholders = (
  data: PlaceholderData,
  usedPlaceholders?: string[]
): PlaceholderStatus[] => {
  const categories = getPlaceholderCategories();
  const results: PlaceholderStatus[] = [];
  
  const { quote, ownerName, organizationKvk, organizationBtw, organizationAddresses, companySettings } = data;
  const company = companySettings || {};
  const primaryAddress = organizationAddresses?.[0];
  
  // Build value map
  const companyStreet = `${company.street || ''} ${company.house_number || ''}`.trim();
  const companyFullAddress = company.street 
    ? `${companyStreet}, ${company.postal_code || ''} ${company.city || ''}`.trim()
    : '';
  
  const orgStreet = primaryAddress 
    ? `${primaryAddress.street || ''} ${primaryAddress.house_number || ''}`.trim()
    : '';
  const orgFullAddress = primaryAddress
    ? `${orgStreet}, ${primaryAddress.postal_code || ''} ${primaryAddress.city || ''}`.trim()
    : '';
  
  const contactFullName = quote.contact 
    ? `${quote.contact.first_name || ''} ${quote.contact.last_name || ''}`.trim()
    : '';
  
  const valueMap: Record<string, string> = {
    // Company
    '{{company_logo}}': company.name ? '[LOGO]' : '', // Special case
    '{{company_name}}': company.name || '',
    '{{company_street}}': companyStreet,
    '{{company_postal_code}}': company.postal_code || '',
    '{{company_city}}': company.city || '',
    '{{company_country}}': company.country || '',
    '{{company_full_address}}': companyFullAddress,
    '{{company_kvk}}': company.kvk || '',
    '{{company_btw}}': company.btw || '',
    '{{company_iban}}': company.iban || '',
    '{{company_bic}}': company.bic || '',
    '{{company_phone}}': company.phone || '',
    '{{company_email}}': company.email || '',
    '{{company_website}}': company.website || '',
    // Organization
    '{{organization_name}}': quote.organization?.name || '',
    '{{organization_kvk}}': organizationKvk || '',
    '{{organization_btw}}': organizationBtw || '',
    '{{organization_phone}}': (quote.organization as any)?.phone || '',
    '{{organization_email}}': (quote.organization as any)?.email || '',
    '{{organization_street}}': orgStreet,
    '{{organization_postal_code}}': primaryAddress?.postal_code || '',
    '{{organization_city}}': primaryAddress?.city || '',
    '{{organization_country}}': primaryAddress?.country || '',
    '{{organization_full_address}}': orgFullAddress,
    // Contact
    '{{contact_name}}': contactFullName,
    '{{contact_first_name}}': quote.contact?.first_name || '',
    '{{contact_last_name}}': quote.contact?.last_name || '',
    '{{contact_email}}': quote.contact?.email || '',
    '{{contact_phone}}': (quote.contact as any)?.phone || '',
    '{{contact_mobile}}': (quote.contact as any)?.mobile || '',
    '{{contact_function}}': (quote.contact as any)?.title || '',
    // Quote
    '{{quote_number}}': quote.quote_number || '',
    '{{quote_title}}': quote.title || '',
    '{{quote_date}}': quote.created_at ? 'filled' : '',
    '{{valid_until}}': quote.valid_until || '',
    '{{reference}}': (quote as any).reference || '',
    '{{version_number}}': String((quote as any).version_number || quote.current_version || 1),
    // Financial - always filled from quote
    '{{subtotal}}': 'filled',
    '{{vat_amount}}': 'filled',
    '{{vat_rate}}': 'filled',
    '{{total}}': 'filled',
    '{{discount_amount}}': 'filled',
    '{{discount_percentage}}': 'filled',
    '{{payment_term}}': 'filled',
    '{{payment_due_date}}': 'filled',
    // Project
    '{{project_name}}': quote.project?.name || '',
    '{{project_number}}': quote.project?.project_number || '',
    // Quote sections
    '{{project_goal}}': quote.project_goal || '',
    '{{work_description}}': quote.work_description || '',
    '{{advice_conclusion}}': quote.advice_conclusion || '',
    '{{additional_conditions}}': quote.additional_conditions || '',
    '{{payment_conditions}}': (quote.payment_conditions && Array.isArray(quote.payment_conditions) && quote.payment_conditions.length > 0) ? 'filled' : '',
    '{{signature_block}}': 'filled', // Always available as special placeholder
    // Line items table - special
    '{{line_items_table}}': 'filled',
    // Misc
    '{{current_date}}': 'filled',
    '{{owner_name}}': ownerName || '',
  };
  
  // Process all categories
  for (const category of categories) {
    for (const ph of category.placeholders) {
      // Skip if we have a usedPlaceholders filter and this isn't in it
      if (usedPlaceholders && !usedPlaceholders.includes(ph.placeholder)) {
        continue;
      }
      
      const value = valueMap[ph.placeholder] || '';
      results.push({
        placeholder: ph.placeholder,
        description: ph.description,
        category: category.label,
        isFilled: !!value,
        value,
      });
    }
  }
  
  return results;
};

export const getPlaceholderCategories = (): PlaceholderCategory[] => {
  return [
    {
      label: 'Eigen Bedrijf',
      placeholders: [
        { placeholder: '{{company_logo}}', description: 'Bedrijfslogo (afbeelding)', example: '[LOGO]' },
        { placeholder: '{{company_name}}', description: 'Eigen bedrijfsnaam', example: 'GreenVision B.V.' },
        { placeholder: '{{company_street}}', description: 'Straat + huisnummer', example: 'Industrieweg 10' },
        { placeholder: '{{company_postal_code}}', description: 'Postcode', example: '1234 AB' },
        { placeholder: '{{company_city}}', description: 'Plaats', example: 'Amsterdam' },
        { placeholder: '{{company_country}}', description: 'Land', example: 'Nederland' },
        { placeholder: '{{company_full_address}}', description: 'Volledig adres', example: 'Industrieweg 10, 1234 AB Amsterdam' },
        { placeholder: '{{company_kvk}}', description: 'KvK nummer', example: '12345678' },
        { placeholder: '{{company_btw}}', description: 'BTW nummer', example: 'NL123456789B01' },
        { placeholder: '{{company_iban}}', description: 'IBAN', example: 'NL00 BANK 1234 5678 90' },
        { placeholder: '{{company_bic}}', description: 'BIC/SWIFT code', example: 'ABNANL2A' },
        { placeholder: '{{company_phone}}', description: 'Telefoonnummer', example: '+31 20 123 4567' },
        { placeholder: '{{company_email}}', description: 'E-mailadres', example: 'info@greenvision.nl' },
        { placeholder: '{{company_website}}', description: 'Website', example: 'www.greenvision.nl' },
      ],
    },
    {
      label: 'Klant Organisatie',
      placeholders: [
        { placeholder: '{{organization_name}}', description: 'Naam organisatie', example: 'Voorbeeld BV' },
        { placeholder: '{{organization_kvk}}', description: 'KVK nummer', example: '12345678' },
        { placeholder: '{{organization_btw}}', description: 'BTW nummer', example: 'NL123456789B01' },
        { placeholder: '{{organization_phone}}', description: 'Telefoonnummer', example: '+31 20 123 4567' },
        { placeholder: '{{organization_email}}', description: 'E-mailadres', example: 'info@klant.nl' },
        { placeholder: '{{organization_street}}', description: 'Straat + huisnummer', example: 'Keizersgracht 100' },
        { placeholder: '{{organization_postal_code}}', description: 'Postcode', example: '1015 CW' },
        { placeholder: '{{organization_city}}', description: 'Plaats', example: 'Amsterdam' },
        { placeholder: '{{organization_country}}', description: 'Land', example: 'Nederland' },
        { placeholder: '{{organization_full_address}}', description: 'Volledig adres', example: 'Keizersgracht 100, 1015 CW Amsterdam' },
      ],
    },
    {
      label: 'Contactpersoon',
      placeholders: [
        { placeholder: '{{contact_name}}', description: 'Volledige naam', example: 'Jan Jansen' },
        { placeholder: '{{contact_first_name}}', description: 'Voornaam', example: 'Jan' },
        { placeholder: '{{contact_last_name}}', description: 'Achternaam', example: 'Jansen' },
        { placeholder: '{{contact_email}}', description: 'E-mailadres', example: 'jan@voorbeeld.nl' },
        { placeholder: '{{contact_phone}}', description: 'Telefoonnummer', example: '+31 6 12345678' },
        { placeholder: '{{contact_mobile}}', description: 'Mobiel nummer', example: '+31 6 12345678' },
        { placeholder: '{{contact_function}}', description: 'Functie', example: 'Inkoop Manager' },
      ],
    },
    {
      label: 'Offerte',
      placeholders: [
        { placeholder: '{{quote_number}}', description: 'Offertenummer', example: 'OFF-2025-001' },
        { placeholder: '{{quote_title}}', description: 'Titel offerte', example: 'Offerte batterijsysteem' },
        { placeholder: '{{quote_date}}', description: 'Datum offerte', example: '15-01-2025' },
        { placeholder: '{{valid_until}}', description: 'Geldig tot', example: '15-02-2025' },
        { placeholder: '{{reference}}', description: 'Uw referentie/kenmerk', example: 'REF-2025-001' },
        { placeholder: '{{version_number}}', description: 'Versienummer', example: '1' },
      ],
    },
    {
      label: 'Financieel',
      placeholders: [
        { placeholder: '{{subtotal}}', description: 'Subtotaal excl. BTW', example: '€10.000,00' },
        { placeholder: '{{vat_amount}}', description: 'BTW bedrag', example: '€2.100,00' },
        { placeholder: '{{vat_rate}}', description: 'BTW percentage', example: '21%' },
        { placeholder: '{{total}}', description: 'Totaal incl. BTW', example: '€12.100,00' },
        { placeholder: '{{discount_amount}}', description: 'Kortingsbedrag', example: '€500,00' },
        { placeholder: '{{discount_percentage}}', description: 'Kortingspercentage', example: '5%' },
        { placeholder: '{{payment_term}}', description: 'Betalingstermijn (dagen)', example: '14' },
        { placeholder: '{{payment_due_date}}', description: 'Uiterste betaaldatum', example: '29-01-2025' },
      ],
    },
    {
      label: 'Project',
      placeholders: [
        { placeholder: '{{project_name}}', description: 'Projectnaam', example: 'Project Amsterdam' },
        { placeholder: '{{project_number}}', description: 'Projectnummer', example: 'PRJ-2025-001' },
      ],
    },
    {
      label: 'Offerte Secties',
      placeholders: [
        { placeholder: '{{project_goal}}', description: 'Projectdoel omschrijving', example: '[PROJECTDOEL]' },
        { placeholder: '{{work_description}}', description: 'Werkomschrijving', example: '[WERKOMSCHRIJVING]' },
        { placeholder: '{{advice_conclusion}}', description: 'Advies en conclusie', example: '[ADVIES]' },
        { placeholder: '{{payment_conditions}}', description: 'Betalingscondities tabel', example: '[BETALINGSCONDITIES]' },
        { placeholder: '{{additional_conditions}}', description: 'Aanvullende voorwaarden', example: '[VOORWAARDEN]' },
        { placeholder: '{{signature_block}}', description: 'Handtekeningblok voor akkoord', example: '[HANDTEKENING]' },
      ],
    },
    {
      label: 'Regelitems',
      placeholders: [
        { placeholder: '{{line_items_table}}', description: 'Volledige tabel met alle regelitems (omschrijving, aantal, prijs, totaal)', example: '[REGELITEMS TABEL]' },
      ],
    },
    {
      label: 'Overig',
      placeholders: [
        { placeholder: '{{current_date}}', description: 'Huidige datum', example: '06-11-2025' },
        { placeholder: '{{owner_name}}', description: 'Eigenaar offerte', example: 'Medewerker Naam' },
      ],
    },
  ];
};
